import { useAuth } from "../../auth/useAuth";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useBasePage } from "../../hooks/useBasePage";
import type { Product } from "../../models/Product";
import type { ProductState, ProductStatusFilter } from "./ProductState";
import { ProductService } from "../../services/ProductService";
import { CategoryService } from "../../services/CategoryService";
import { DataState } from "../../models/DataState";
import { errorMessage } from "../../utils/ErrorMessage";
import { PrepareImageFile } from "../../utils/ImageFile";

import { UnitService } from "../../services/UnitService";
import type { Unit } from "../../models/Unit";
import { loadAllLookupPages } from "../../hooks/useInventoryLookups";
import { validateProductUnits } from "../../utils/ProductUnits";

const initialState: ProductState = {
  loading: true,
  loadError: "",
  searchText: "",
  category: "All",
  status: "All",
  skip: 0,
  limit: 20,
  total: 0,
  isDialogOpen: false,
  dialogMode: "Create",
  selectedProduct: {
    DataState: DataState.New,
    Code: "",
    Name: "",
    Description: "",
    Barcode: "",
    Categories: [],
    Unit: "",
    BaseUnitCode: "",
    UnitPrices: [],
    DiscountedSellingPrice: 0,
    CostPrice: 0,
    CurrencyCode: "NGN",
    ReorderLevel: 0,
    Status: "Active",
    CategoryData: [],
    Images: [],
    TrackInventory: true,
    AllowOutOfStockOrder: false,
    AllowCreditSale: false,
    IsActive: true,
    IsVisibleOnStorefront: true,
  },
  products: [],
  categories: [],
  successMessage: "",
};

export function useProduct() {
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitError, setUnitError] = useState("");
  useEffect(() => { void loadAllLookupPages(UnitService().GetAll).then(setUnits).catch(()=>setUnitError("Could not load units. Check that UnitService is registered on the backend.")); }, []);
  const { hasPermission, state: authState } = useAuth();
  const allowed = (action: string) =>
    hasPermission(`Product.${action}`) ||
    (authState.RoleUis === undefined && hasPermission("Product.Manage"));
  const { state, setState, setStateProperty, setStateProperties } =
    useBasePage<ProductState>(initialState);
  const { skip, limit, products } = state;
  const productService = ProductService();
  const searchSequence = useRef(0);
  const categoryService = CategoryService();

  const initialize = useCallback(async (): Promise<void> => {
    setStateProperties({loading: true, loadError: ""});
    try {
    const data = await productService.GetAll(skip, limit);
    setStateProperties({
      skip: data.Skipped,
      limit: data.Limit,
      products: data.Data,
      total: data.TotalRecords,
      loading: false,
    });
    } catch (error) { setStateProperties({loading: false, loadError: errorMessage(error, "Could not load products. Retry the request.")}); }
  }, [products]);
  const fetAllCategories = useCallback(async (): Promise<void> => {
    const data = await categoryService.GetActivePartial(skip, limit);
    setStateProperty("categories", data.Data);
  }, [state.categories]);
  useEffect(() => {
    void fetAllCategories().catch(() => {});
  }, []);
  useEffect(() => {
    const reload = (): void => {
      void initialize();
    };
    window.addEventListener("pantrystack:navigation-reselect", reload);
    return () =>
      window.removeEventListener("pantrystack:navigation-reselect", reload);
  }, []);

  useEffect(() => {
    const sequence = ++searchSequence.current;
    const timer = window.setTimeout(async () => {
      setStateProperties({ loading: true, loadError: "" });
      try {
        const data = state.searchText.trim()
          ? await productService.SearchByAnyParam(state.searchText.trim(), 0, state.limit)
          : await productService.GetAll(0, state.limit);
        if (sequence === searchSequence.current)
          setStateProperties({ products: data.Data, skip: data.Skipped ?? 0, total: data.TotalRecords, loading: false });
      } catch (error) {
        if (sequence === searchSequence.current)
          setStateProperties({ loading: false, loadError: errorMessage(error, "Could not search products.") });
      }
    }, 400);
    return () => { window.clearTimeout(timer); ++searchSequence.current; };
  }, [state.searchText]);

  const filteredProducts = useMemo(() => {


    return products.filter((product) => {
      // The API searches all records, including referenced fields.
      const matchesSearch = true;

      const matchesCategory =
        state.category === "All" || product.Categories.includes(state.category);

      const matchesStatus =
        state.status === "All" || product.Status === state.status;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, state.searchText, state.category, state.status]);

  const summary = useMemo(
    () => ({
      Total: state.total,
      Active: products.filter((product) => product.Status === "Active").length,
      LowStock: products.filter((product) => product.Status === "LowStock")
        .length,
      OutOfStock: products.filter((product) => product.Status === "OutOfStock")
        .length,
    }),
    [products, state.total],
  );

  const changeFilter = (
    property: "category" | "status",
    value: string,
  ): void => {
    setStateProperties({
      [property]: value,
      currentPage: 1,
    } as Partial<ProductState>);
  };

  const changeStatus = (status: ProductStatusFilter): void => {
    changeFilter("status", status);
  };

  const openCreateDialog = (): void => {
    if (!allowed("Create")) return;
    setUnitError("");
    setStateProperties({
      dialogMode: "Create",
      isDialogOpen: true,
      selectedProduct: { ...initialState.selectedProduct, UnitPrices: [], Categories: [], Images: [] },
    });
  };

  const openEditDialog = (product: Product): void => {
    if (!allowed("Edit")) return;
    setUnitError("");
    setStateProperties({
      dialogMode: "Edit",
      isDialogOpen: true,
      selectedProduct: {
        ...product,
        DataState: product.DataState ?? DataState.Unchanged,
        Categories: Array.isArray(product.Categories) ? product.Categories : [],
        Images: product.Images ?? [],
        UnitPrices: (product.UnitPrices ?? []).map(x=>({...x, DiscountedSellingPrice: x.DiscountedSellingPrice ?? (x.IsDefaultSaleUnit && product.DiscountedSellingPrice > 0 ? product.DiscountedSellingPrice : null)})),
        DiscountedSellingPrice: 0,
        BaseUnitCode: product.BaseUnitCode || product.UnitPrices?.find(x=>x.BaseQuantity===1)?.UnitCode || "",
      },
    });
  };

  const closeDialog = (): void => {
    setStateProperty("isDialogOpen", false);
  };

  const setProductProperty = <K extends keyof Product>(
    property: K,
    value: Product[K],
  ): void => {
    setUnitError("");
    setStateProperty("selectedProduct", {
      ...state.selectedProduct,
      [property]: value,
      DataState:
        state.selectedProduct.DataState === DataState.Unchanged
          ? DataState.Changed
          : state.selectedProduct.DataState,
    });
  };

  const saveProduct = async (): Promise<void> => {
    if (!allowed(state.dialogMode === "Create" ? "Create" : "Edit")) return;
    const error = !state.selectedProduct.Name.trim() ? "Enter a product name." : !state.selectedProduct.Categories.length ? "Select at least one category." : validateProductUnits(state.selectedProduct);
    if (error) { setUnitError(error); return; }
    if (state.selectedProduct.UnitPrices.some(row=>row.IsActive && !units.some(unit=>unit.Code===row.UnitCode && unit.IsActive))) {
      setUnitError("Every active product unit must exist in the active unit catalogue. Reload units or choose an active unit.");
      return;
    }
    const savedProducts = await productService.Save([state.selectedProduct]);

    const normalizedProducts = savedProducts.map((product) => ({
      ...product,
      DataState: DataState.Unchanged,
    }));

    const savedProductsByCode = new Map(
      normalizedProducts.map((product) => [product.Code, product]),
    );

    const updatedProducts = state.products.map((product) => {
      return savedProductsByCode.get(product.Code) ?? product;
    });

    const existingCodes = new Set(
      state.products.map((product) => product.Code),
    );

    const newProducts = normalizedProducts.filter(
      (product) => !existingCodes.has(product.Code),
    );

    setStateProperties({
      products: [...newProducts, ...updatedProducts],
      total: state.total + newProducts.length,
      isDialogOpen: false,
      successMessage:
        state.dialogMode === "Create"
          ? "Product created successfully."
          : "Product updated successfully.",
    });
  };

  const removeProduct = async (product: Product): Promise<void> => {
    if (!allowed("Delete")) return;
    await productService.Save([{ ...product, DataState: DataState.Remove }]);
    setStateProperty(
      "products",
      state.products.filter((item) => item.Code !== product.Code),
    );
    setStateProperty("successMessage", "Product deleted successfully.");
    setStateProperty("total", Math.max(0, state.total - 1));
  };

  const previousPage = async (): Promise<void> => {
    const nextSkip = Math.max(0, state.skip - state.limit);
    const data = state.searchText.trim() ? await productService.SearchByAnyParam(state.searchText.trim(), nextSkip, state.limit) : await productService.GetAll(nextSkip, state.limit);
    setStateProperties({
      products: data.Data,
      skip: data.Skipped,
      total: data.TotalRecords,
    });
  };
  const nextPage = async (): Promise<void> => {
    const data = state.searchText.trim()
      ? await productService.SearchByAnyParam(state.searchText.trim(), state.skip + state.limit, state.limit)
      : await productService.GetAll(state.skip + state.limit, state.limit);
    setStateProperties({
      products: data.Data,
      skip: data.Skipped,
      total: data.TotalRecords,
    });
  };

  const addProductImages = async (files: File[]): Promise<void> => {
    const preparedFiles = await Promise.all(files.map(PrepareImageFile));
    const hasPrimary = state.selectedProduct.Images.some(
      (image) => image.IsPrimary,
    );
    const images = preparedFiles.map((file, index) => ({
      ImageFile: file,
      Url: URL.createObjectURL(file),
      IsPrimary: !hasPrimary && index === 0,
      DisplayOrder: state.selectedProduct.Images.length + index,
    }));
    setProductProperty("Images", [...state.selectedProduct.Images, ...images]);
  };

  const removeProductImage = (index: number): void => {
    const removed = state.selectedProduct.Images[index];
    if (removed?.ImageFile && removed.Url) URL.revokeObjectURL(removed.Url);
    const images = state.selectedProduct.Images.filter(
      (_, imageIndex) => imageIndex !== index,
    );
    if (removed?.IsPrimary && images.length > 0) {
      images[0] = { ...images[0], IsPrimary: true };
    }
    setProductProperty(
      "Images",
      images.map((image, imageIndex) => ({
        ...image,
        DisplayOrder: imageIndex,
      })),
    );
  };

  const setPrimaryProductImage = (index: number): void => {
    setProductProperty(
      "Images",
      state.selectedProduct.Images.map((image, imageIndex) => ({
        ...image,
        IsPrimary: imageIndex === index,
      })),
    );
  };

  const replaceProductImage = async (
    index: number,
    file?: File,
  ): Promise<void> => {
    if (!file) return;
    const prepared = await PrepareImageFile(file);
    const images = [...state.selectedProduct.Images];
    const previous = images[index];
    if (previous?.ImageFile && previous.Url) URL.revokeObjectURL(previous.Url);
    images[index] = {
      ...previous,
      ImageFile: prepared,
      Url: URL.createObjectURL(prepared),
    };
    setProductProperty("Images", images);
  };

  return {
    reload: initialize,
    state,
    units,
    unitError,
    filteredProducts,
    summary,
    setStateProperty,
    changeFilter,
    changeStatus,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    setProductProperty,
    saveProduct,
    addProductImages,
    removeProductImage,
    setPrimaryProductImage,
    replaceProductImage,
    removeProduct,
    previousPage,
    nextPage,
  };
}
